import type {
  CreateOrderSetBody,
  OrderSetDetail,
  OrderSetDiagnosis,
  OrderSetItem,
  OrderSetItemInput,
  OrderSetSummary,
  UpdateOrderSetBody,
} from '@/types/order-set-api';

/**
 * The add / edit modal's draft (PROD-2710, PROD-2714). Pure: the modal owns the state, these
 * functions own the rules, so the wire body is testable without rendering.
 *
 * An item keeps the server's own fields exactly as they came (`itemId`, `type`, `code`, `details`)
 * unless the clinician changes them: pressing the row rewrites `details` from the A/P order form, the tick
 * rewrites `addByDefault`. A new item has no `itemId` (the server mints one); `key` is the draft's
 * own stable id for it. Order is the order added: the design has no reorder.
 */
export interface OrderSetDraftItem extends Omit<OrderSetItem, 'itemId'> {
  /** Absent on an item added in this draft. */
  itemId?: string;
  /** Stable draft id: the `itemId`, or a local one for a new item. */
  key: string;
}

export interface OrderSetDraft {
  name: string;
  diagnoses: OrderSetDiagnosis[];
  items: OrderSetDraftItem[];
}

export function emptyDraft(): OrderSetDraft {
  return { name: '', diagnoses: [], items: [] };
}

export function draftFromDetail(detail: OrderSetDetail): OrderSetDraft {
  return {
    name: detail.name,
    diagnoses: [...detail.diagnoses],
    items: [...detail.items]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => ({ ...item, key: item.itemId })),
  };
}

let localSeq = 0;
/** A draft key for an item added here (never sent). */
export function newItemKey(): string {
  localSeq += 1;
  return `new-${localSeq}`;
}

/** Append a new order, ticked "Add by default" (a search pick lands as a default order). */
export function addItem(
  draft: OrderSetDraft,
  item: Pick<OrderSetItem, 'type' | 'code' | 'details'>,
): OrderSetDraft {
  const next: OrderSetDraftItem = {
    key: newItemKey(),
    type: item.type,
    code: item.code,
    details: item.details,
    addByDefault: true,
    sortOrder: 0,
  };
  return { ...draft, items: [...draft.items, next] };
}

/**
 * The wire items. The server-computed `orderable` / `notOrderableReason` are never sent; a new item
 * goes without `itemId`.
 *
 * `sortOrder` is the order added. A kept item sends its own value (they are already ascending,
 * since nothing reorders), so an unchanged save sends every `sortOrder` unchanged whatever base the
 * server numbers from; each new item takes the next number after the highest one.
 */
function toInputs(draft: OrderSetDraft): OrderSetItemInput[] {
  let next = draft.items.reduce((m, i) => (i.itemId ? Math.max(m, i.sortOrder) : m), 0);
  return draft.items.map((item) => {
    const sortOrder = item.itemId ? item.sortOrder : (next += 1);
    return {
      ...(item.itemId ? { itemId: item.itemId } : {}),
      type: item.type,
      addByDefault: item.addByDefault,
      sortOrder,
      code: item.code,
      details: item.details,
    };
  });
}

/** The POST body for a new set. */
export function buildCreateBody(draft: OrderSetDraft): CreateOrderSetBody {
  return { name: draft.name.trim(), diagnoses: draft.diagnoses, items: toInputs(draft) };
}

/** The PUT body, from the version the edit started on. */
export function buildUpdateBody(detail: OrderSetDetail, draft: OrderSetDraft): UpdateOrderSetBody {
  return { ...buildCreateBody(draft), expectedVersion: detail.version };
}

export function isDraftDirty(detail: OrderSetDetail, draft: OrderSetDraft): boolean {
  const base = buildUpdateBody(detail, draftFromDetail(detail));
  return JSON.stringify(base) !== JSON.stringify(buildUpdateBody(detail, draft));
}

/** Validation. The API needs a name and at least one order. */
export function draftError(draft: OrderSetDraft): string | null {
  if (draft.name.trim().length === 0) return 'Name is required.';
  if (draft.items.length === 0) return 'An order set needs at least one order.';
  return null;
}

/** "Orders (N) · M added by default", the Orders header. */
export function ordersHeader(draft: Pick<OrderSetDraft, 'items'>): string {
  const byDefault = draft.items.filter((i) => i.addByDefault).length;
  return `Orders (${draft.items.length}) · ${byDefault} added by default`;
}

/**
 * The card's meta line (boards 1 and 2): "8 orders (2 optional) · Updated 24 Sep 2026", "9 orders ·
 * Updated 11 Sep 2026" (no optional ones: no brackets), "8 orders · Archived 2 Sep 2026".
 * `optionalCount` is PROD-2711's; until it deploys the card leaves "(M optional)" out.
 */
export function cardMeta(set: OrderSetSummary, archived: boolean, day: string): string {
  const optional = archived ? 0 : set.optionalCount ?? 0;
  const orders = `${set.itemCount} ${set.itemCount === 1 ? 'order' : 'orders'}${optional > 0 ? ` (${optional} optional)` : ''}`;
  return day ? `${orders} · ${archived ? 'Archived' : 'Updated'} ${day}` : orders;
}

/** "1 order no longer orderable" / "2 orders no longer orderable" (board 1). */
export function notOrderableText(n: number): string {
  return `${n} ${n === 1 ? 'order' : 'orders'} no longer orderable`;
}
